// hooks/features/chatbot/use-chatbot.ts
import { useAuth } from "@/src/contexts/auth.context";
import apiClient from "@/src/services/api/axios";
import {
  useCreateAgent,
  useCreateKbFolder,
  useGetChatbotConfig,
  useUpdateChatbotConfig,
} from "@/src/services/api/chatbot/chatbot.hooks";
import { useState } from "react";

export const useChatbot = () => {
  const { orgId } = useAuth();
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const { mutateAsync: createAgent } = useCreateAgent();
  const { mutateAsync: createKbFolder } = useCreateKbFolder();
  const { mutateAsync: updateChatbotConfig } = useUpdateChatbotConfig();

  const { data: chatbotConfig, isPending: isConfigLoading } =
    useGetChatbotConfig();

  // Enable chatbot
  const handleSetupChatbot = async () => {
    if (!orgId) {
      console.warn("handleSetupChatbot called without orgId");
      return;
    }
    const name = `${orgId}-WikiBot`;
    setIsSettingUp(true);

    try {
      const [agentRes, kbRes] = await Promise.all([
        createAgent({ name }),
        createKbFolder({ name }),
      ]);

      const agentDetails = await apiClient.post(
        `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/blocksai-api/v1/agents/queries`,
        {
          id: agentRes.item_id,
          project_key: process.env.EXPO_PUBLIC_X_BLOCKS_KEY,
        },
      );

      const widgetId = agentDetails.data.agents[0]?.widget_id;

      await apiClient.post(
        `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/blocksai-api/v1/agents/update-ai-configurations`,
        {
          id: agentRes.item_id,
          folder_ids: [kbRes.data.folder_id],
          agent_id: agentRes.item_id,
          project_key: process.env.EXPO_PUBLIC_X_BLOCKS_KEY,
        },
      );

      await updateChatbotConfig({
        orgId: orgId!,
        itemId: agentRes.item_id,
        kbFolderId: kbRes.data.folder_id,
        widgetId,
      });
    } catch (e) {
      console.error("Chatbot setup failed:", e);
    } finally {
      setIsSettingUp(false);
    }
  };

  // Disable chatbot
  const handleDisableChatbot = async () => {
    if (!chatbotConfig?.itemId || !chatbotConfig?.kbFolderId) {
      console.warn("No chatbot enabled");
      return;
    }

    setIsDisabling(true);

    try {
      const projectKey = process.env.EXPO_PUBLIC_X_BLOCKS_KEY;

      await Promise.all([
        apiClient.delete(
          `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/blocksai-api/v1/agents/delete/${chatbotConfig.itemId}?project_key=${projectKey}`,
        ),

        apiClient.delete(
          `${process.env.EXPO_PUBLIC_BLOCKS_BASE_URL}/blocksai-api/v1/kb/folder/delete?project_key=${projectKey}&folder_id=${chatbotConfig.kbFolderId}`,
        ),
      ]);

      await updateChatbotConfig({
        orgId: orgId!,
        itemId: "",
        kbFolderId: "",
        widgetId: "",
      });
    } catch (e) {
      console.error("Failed to disable chatbot:", e);
    } finally {
      setIsDisabling(false);
    }
  };

  return {
    isSettingUp,
    isDisabling,
    handleSetupChatbot,
    handleDisableChatbot,
    chatbotConfig,
    isConfigLoading,
  };
};
